/**
 * Programa de Parceiros (docs/15): página pública /parceiros com candidatura,
 * termos, link de parceiro /p/:code, entrada por ligação no email e painel do
 * parceiro. O painel (AffiliatePanelView) também aparece na app de um cliente
 * que aderiu (Definições → Subscrição e pagamento).
 */
import {
  BadgePercent, Download, FileText, Landmark, Megaphone, Plus, Printer, QrCode, Smartphone, Briefcase, Building2, CalendarClock, Check, Copy, Handshake, LogIn, Mail, MessageCircle, MousePointerClick, Repeat, Share2, Store, Users, Wallet,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { Alert, Badge, Button, Checkbox, Field, Input, Loading, Select, Textarea } from '../components/ui';
import { ApiError, api, get, post } from '../lib/api';
import { cn } from '../lib/cn';
import { date, mt } from '../lib/format';
import { btn, CONTACT, PageHero } from './portal';
import { waMe } from '@makila/shared';

// ── Link de parceiro: guardado 90 dias no browser (último clique) ───────────
const REF_KEY = 'makila.ref';
const REF_DAYS = 90;
export function saveReferral(code: string) {
  try { localStorage.setItem(REF_KEY, JSON.stringify({ code: code.toUpperCase(), at: Date.now() })); } catch { /* sem armazenamento: o código pode ser escrito no registo */ }
}
export function readReferral(): string | null {
  try {
    const v = JSON.parse(localStorage.getItem(REF_KEY) ?? 'null') as { code: string; at: number } | null;
    if (!v || Date.now() - v.at > REF_DAYS * 86_400_000) return null;
    return /^[A-Z0-9][A-Z0-9-]{2,29}$/.test(v.code) ? v.code : null;
  } catch { return null; }
}
/** `?ref=CODIGO` em qualquer página do site: guarda e conta o clique. */
export function captureRefParam(search: string) {
  const code = new URLSearchParams(search).get('ref')?.trim().toUpperCase();
  if (!code || !/^[A-Z0-9][A-Z0-9-]{2,29}$/.test(code)) return;
  saveReferral(code);
  void post('/public/affiliates/click', { code }).catch(() => {});
}

/** /p/:code — o link que o parceiro partilha. */
export function PartnerLinkPage() {
  const { code = '' } = useParams();
  const c = code.trim().toUpperCase();
  useEffect(() => {
    if (!/^[A-Z0-9][A-Z0-9-]{2,29}$/.test(c)) return;
    saveReferral(c);
    void post('/public/affiliates/click', { code: c }).catch(() => {});
  }, [c]);
  return <Navigate to="/" replace />;
}

/** Faixa no topo do site quando o visitante veio por um parceiro. */
export function ReferralBanner() {
  const [who, setWho] = useState<{ name: string; welcome_pct: string } | null>(null);
  useEffect(() => {
    const code = readReferral();
    if (code) get<{ affiliate: { name: string; welcome_pct: string } | null }>(`/public/affiliates/code/${code}`).then((r) => setWho(r.affiliate)).catch(() => {});
  }, []);
  if (!who) return null;
  return (
    <div className="bg-brand-700 text-white">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center text-sm sm:px-6">
        <BadgePercent className="size-4 shrink-0" />
        <span>Recomendado por <b>{who.name}</b>: {who.welcome_pct}% de desconto no primeiro pagamento.</span>
        <Link to="/registar" className="font-semibold underline underline-offset-2">Criar conta grátis</Link>
      </div>
    </div>
  );
}

// ── /parceiros ──────────────────────────────────────────────────────────────
const KINDS: { id: 'accountant' | 'reseller' | 'individual'; icon: ReactNode; title: string; text: string }[] = [
  { id: 'accountant', icon: <Briefcase />, title: 'Contabilistas e gabinetes', text: 'Os seus clientes precisam de facturação legal. Recomende o Makila e ganhe sobre cada empresa que entrar.' },
  { id: 'reseller', icon: <Store />, title: 'Revendedores de TI', text: 'Instala computadores, redes ou software? Junte o Makila à sua oferta e dê o primeiro apoio aos seus clientes.' },
  { id: 'individual', icon: <Users />, title: 'Consultores e formadores', text: 'Forma ou aconselha empresários? Partilhe o seu link nas formações, grupos e redes sociais.' },
];

/**
 * Marca de água: o símbolo Makila (recibo serrilhado com o «M») só em traço,
 * na cor do texto (currentColor) — discreto em fundo claro ou escuro.
 */
function Watermark({ className }: { className?: string }) {
  return (
    <svg viewBox="100 60 312 400" aria-hidden className={cn('pointer-events-none absolute select-none', className)} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
      <path d="M132 120 Q132 92 160 92 L352 92 Q380 92 380 120 L380 404 L359.3 430 L338.7 404 L318 430 L297.3 404 L276.7 430 L256 404 L235.3 430 L214.7 404 L194 430 L173.3 404 L152.7 430 L132 404 Z" strokeWidth="10" />
      <path d="M178 300 L178 160 L238 236 L334 132 L334 300" strokeWidth="26" />
      <path d="M185 343 H327 M185 371 H267 M299 371 H327" strokeWidth="12" />
    </svg>
  );
}

/** Moldura de browser / telemóvel para as capturas reais do sistema (dados de demonstração). */
function BrowserFrame({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-xl bg-white shadow-2xl shadow-black/20 ring-1 ring-black/10', className)}>
      <div className="flex items-center gap-1.5 border-b border-black/[.06] bg-[#f6f6f7] px-3 py-2">
        <span className="size-2.5 rounded-full bg-[#ff5f57]" /><span className="size-2.5 rounded-full bg-[#febc2e]" /><span className="size-2.5 rounded-full bg-[#28c840]" />
        <span className="ml-3 truncate rounded-md bg-white px-2 py-0.5 text-[10px] text-[#5d5d63] ring-1 ring-black/[.06]">ferragens-zambeze.makila.sistemamz.com</span>
      </div>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </div>
  );
}
function PhoneFrame({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return (
    <div className={cn('overflow-hidden rounded-[1.6rem] border-[6px] border-[#111] bg-white shadow-2xl shadow-black/30', className)}>
      <img src={src} alt={alt} loading="lazy" className="block w-full" />
    </div>
  );
}

export function PortalPartners() {
  return (
    <>
      {/* ── Topo: proposta + o sistema que vai recomendar ─────────── */}
      <section className="relative overflow-hidden border-b border-black/[.06] bg-[#fafafa]">
        <div aria-hidden className="bg-dots absolute inset-0 [mask-image:linear-gradient(to_bottom,#000,transparent)]" />
        <Watermark className="-left-16 -top-10 w-[340px] -rotate-6 text-brand-700/[.07]" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-14 sm:px-6 sm:py-16 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <div className="mb-3 text-[13px] font-semibold uppercase tracking-[.16em] text-brand-700">Programa de Parceiros</div>
            <h1 className="font-display text-4xl font-bold leading-tight sm:text-5xl">Recomende o Makila. <span className="text-brand-700">Ganhe 20% durante um ano.</span></h1>
            <p className="mt-4 max-w-xl text-lg text-[#5d5d63]">Por cada empresa que se registar pelo seu link e pagar a subscrição, recebe 20% de cada pagamento dela durante 12 meses. E a empresa ganha 10% no primeiro pagamento.</p>
            <div className="mt-8 flex flex-col gap-3 whitespace-nowrap sm:flex-row">
              <a href="#candidatura" className={btn.primary}>Quero ser parceiro <Handshake className="size-4" /></a>
              <a href="#simulador" className={btn.ghost}>Quanto posso ganhar?</a>
              <a href={waMe(CONTACT.phone, 'Olá! Quero saber mais sobre o Programa de Parceiros Makila.') ?? '#'} target="_blank" rel="noopener noreferrer" className={cn(btn.ghost, 'text-[#128c4a]')}><MessageCircle className="size-4" />Dúvidas? WhatsApp</a>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#5d5d63]">
              {['Sem custos de adesão', 'Não precisa de ser assinante', 'Recebe por M-Pesa ou banco'].map((t) => <span key={t} className="inline-flex items-center gap-1.5"><Check className="size-4 text-emerald-600" />{t}</span>)}
            </div>
          </div>
          <div className="relative mx-auto w-full max-w-xl pb-10 lg:pb-0">
            <BrowserFrame src="/site/painel.webp" alt="Painel do Makila com vendas, IVA e valores a receber" className="rotate-[-1.5deg]" />
            <PhoneFrame src="/site/movel-painel.webp" alt="O Makila no telemóvel" className="absolute -bottom-2 -right-2 w-[30%] rotate-[3deg] sm:-right-6" />
            <div className="absolute -left-3 bottom-6 flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-xl ring-1 ring-black/[.06] sm:-left-8">
              <span className="grid size-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-700"><Wallet className="size-5" /></span>
              <div><div className="text-xs text-[#5d5d63]">Comissão disponível</div><div className="font-display text-lg font-bold tabular-nums">+ 290 MT <span className="text-xs font-medium text-[#5d5d63]">/ empresa / mês</span></div></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Como funciona ─────────────────────────────────────────── */}
      <section className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6">
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { icon: <Share2 />, t: '1. Partilhe o seu link', d: 'Recebe um link, um código e um QR só seus, com mensagens prontas para WhatsApp, email e redes.' },
            { icon: <Building2 />, t: '2. A empresa regista-se e paga', d: 'Quem entra pelo seu link fica associado a si e tem 10% de desconto no primeiro pagamento.' },
            { icon: <Wallet />, t: '3. Recebe a comissão', d: '20% de cada pagamento durante 12 meses, por M-Pesa, e-Mola, transferência ou crédito na sua subscrição.' },
          ].map((s, i) => (
            <div key={s.t} className="relative overflow-hidden rounded-2xl bg-white p-6 ring-1 ring-black/[.08]">
              <span aria-hidden className="absolute -right-2 -top-6 font-display text-[110px] font-bold leading-none text-black/[.035]">{i + 1}</span>
              <span className="relative grid size-11 place-items-center rounded-xl bg-brand-700 text-white [&>svg]:size-5">{s.icon}</span>
              <h3 className="relative mt-4 font-display text-lg font-bold">{s.t}</h3>
              <p className="relative mt-1.5 text-[15px] leading-relaxed text-[#5d5d63]">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Simulador ─────────────────────────────────────────────── */}
      <section id="simulador" className="relative scroll-mt-20 overflow-hidden border-y border-black/[.06] bg-[#fafafa]">
        <Watermark className="-right-16 -top-6 w-[380px] rotate-6 text-brand-700/[.06]" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6">
          <div className="mb-8 max-w-2xl">
            <div className="mb-2 text-[13px] font-semibold uppercase tracking-[.16em] text-brand-700">Simulador</div>
            <h2 className="font-display text-3xl font-bold">Quanto pode ganhar?</h2>
            <p className="mt-2 text-[#5d5d63]">Escolha quantas empresas traz, o plano e como pagam. O cálculo aparece passo a passo.</p>
          </div>
          <EarningsCalculator />
        </div>
      </section>

      {/* ── O que vai recomendar (sistema real) ───────────────────── */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_1.15fr]">
          <div>
            <div className="mb-2 text-[13px] font-semibold uppercase tracking-[.16em] text-brand-700">O que vai recomendar</div>
            <h2 className="font-display text-3xl font-bold leading-tight">Um produto fácil de recomendar — porque resolve um problema real.</h2>
            <p className="mt-3 text-[#5d5d63]">As empresas precisam de facturar de acordo com as regras da AT. O Makila faz isso no telemóvel ou no computador, e o seu cliente começa no mesmo dia.</p>
            <ul className="mt-6 space-y-3">
              {[
                [<FileText key="f" />, 'Facturas e recibos com QR verificável', 'Documentos numerados e assinados, prontos a enviar por email ou WhatsApp.'],
                [<Landmark key="l" />, 'Ficheiro da AT num clique', 'O ficheiro mensal para o e-Declaração sai sem copiar nada à mão.'],
                [<Smartphone key="s" />, 'M-Pesa, e-Mola e mKesh', 'Os pagamentos ficam registados e as contas correntes em dia.'],
              ].map(([icon, t, d]) => (
                <li key={t as string} className="flex gap-4">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-700/[.08] text-brand-700 [&>svg]:size-5">{icon}</span>
                  <div><div className="font-semibold">{t}</div><div className="text-[15px] text-[#5d5d63]">{d}</div></div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div aria-hidden className="absolute -inset-6 -z-10 rounded-[2rem] bg-gradient-to-br from-brand-700/10 via-transparent to-transparent" />
            <BrowserFrame src="/site/factura-emitida.webp" alt="Factura emitida no Makila, com pré-visualização e resumo" />
            <p className="mt-3 text-center text-xs text-[#8a8a90]">Imagens reais do Makila com dados de demonstração.</p>
          </div>
        </div>
      </section>

      {/* ── Para quem é + área de parceiro ────────────────────────── */}
      <section className="relative overflow-hidden bg-[#111] text-white">
        <Watermark className="-bottom-20 -left-16 w-[380px] -rotate-6 text-white/[.06]" />
        <div className="relative mx-auto grid max-w-6xl gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1fr_1fr]">
          <div>
            <h2 className="font-display text-3xl font-bold">Para quem é</h2>
            <div className="mt-6 space-y-5">
              {KINDS.map((k) => (
                <div key={k.id} className="flex gap-4">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/10 text-brand-300 [&>svg]:size-5">{k.icon}</span>
                  <div><div className="font-semibold">{k.title}</div><p className="text-[15px] leading-relaxed text-white/60">{k.text}</p></div>
                </div>
              ))}
              <div className="flex gap-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/10 text-brand-300"><Handshake className="size-5" /></span>
                <div><div className="font-semibold">Já é cliente Makila?</div><p className="text-[15px] leading-relaxed text-white/60">Adira num clique em <b className="text-white">Definições → Subscrição e pagamento</b>, dentro da aplicação da sua empresa.</p></div>
              </div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-3xl font-bold">A sua área de parceiro</h2>
            <p className="mt-2 text-white/60">Tudo num só sítio, mesmo sem ser assinante — entra com uma ligação enviada ao seu email.</p>
            <PanelPreview />
          </div>
        </div>
      </section>

      {/* ── Perguntas e candidatura ───────────────────────────────── */}
      <section id="candidatura" className="relative scroll-mt-20 overflow-hidden">
        <Watermark className="-left-20 bottom-10 w-[320px] -rotate-6 text-brand-700/[.06]" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1.15fr]">
          <div>
            <div className="mb-2 text-[13px] font-semibold uppercase tracking-[.16em] text-brand-700">Perguntas frequentes</div>
            <h2 className="mb-6 font-display text-3xl font-bold">Antes de se candidatar</h2>
            <Accordion items={PARTNER_FAQ} />
            <Link to="/parceiro/entrar" className="mt-8 inline-flex items-center gap-2 font-semibold text-brand-700 hover:underline"><LogIn className="size-4" />Já é parceiro? Entrar na área de parceiro</Link>
          </div>
          <ApplicationForm />
        </div>
      </section>
    </>
  );
}

const PARTNER_FAQ: { q: string; a: ReactNode }[] = [
  { q: 'Quanto ganho, exactamente?', a: <>20% do que a empresa que trouxer pagar pelos seus primeiros 12 meses de subscrição. Uma empresa no plano Empresa (1 450 MT/mês) dá-lhe <b>290 MT por mês</b>; se pagar o ano de uma vez, recebe a comissão do ano inteiro de uma vez. Se pagar mais de 12 meses de uma vez, a comissão conta só os primeiros 12.</> },
  { q: 'Quando recebo?', a: 'Cada comissão fica disponível 30 dias depois do pagamento da empresa (o prazo cobre reembolsos). Pagamos a partir de 1 000 MT acumulados.' },
  { q: 'Como recebo?', a: 'Por M-Pesa, e-Mola, mKesh ou transferência bancária — indica os dados na área de parceiro. Se também for cliente Makila, pode preferir crédito na sua subscrição.' },
  { q: 'Há impostos?', a: 'Sim. Às comissões pagas a pessoas singulares aplica-se a retenção na fonte de IRPS prevista na lei, que entregamos às Finanças em seu nome. Empresas parceiras passam factura e não têm retenção.' },
  { q: 'Preciso de ser assinante do Makila?', a: 'Não. Qualquer pessoa ou empresa aprovada pode ser parceira. A área de parceiro abre com uma ligação enviada ao seu email, sem password.' },
  { q: 'E se a empresa já me conhecia?', a: 'Conta o link pelo qual se registou (até 90 dias depois do clique) ou o código escrito no registo ou no primeiro pagamento.' },
  { q: 'Posso usar o meu próprio link?', a: 'Não. Uma empresa sua, com o seu NUIT ou o seu email, não gera comissão nem desconto.' },
];

/** Acordeão moderno: abre e fecha com animação de altura (grid 0fr → 1fr), um de cada vez. */
export function Accordion({ items, dark }: { items: { q: string; a: ReactNode }[]; dark?: boolean }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="space-y-3">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={f.q} className={cn('overflow-hidden rounded-2xl ring-1 transition-shadow',
            dark ? 'bg-white/[.04] ring-white/10' : 'bg-white ring-black/[.08]', isOpen && (dark ? 'ring-white/25' : 'shadow-lg shadow-black/[.06] ring-brand-700/25'))}>
            <button type="button" aria-expanded={isOpen} aria-controls={`faq-${i}`} onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left font-semibold sm:px-6">
              <span>{f.q}</span>
              <span className={cn('grid size-8 shrink-0 place-items-center rounded-full transition-all duration-300',
                isOpen ? 'rotate-45 bg-brand-700 text-white' : dark ? 'bg-white/10 text-white' : 'bg-black/[.05] text-[#111]')}><Plus className="size-4" /></span>
            </button>
            <div id={`faq-${i}`} role="region" className={cn('grid transition-[grid-template-rows] duration-300 ease-out', isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]')}>
              <div className="overflow-hidden">
                <div className={cn('px-5 pb-5 leading-relaxed sm:px-6', dark ? 'text-white/70' : 'text-[#3A3A3B]')}>{f.a}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Pré-visualização ilustrativa da área de parceiro (valores de exemplo). */
function PanelPreview() {
  return (
    <div className="mt-6 overflow-hidden rounded-2xl bg-white text-[#111] shadow-2xl shadow-black/40">
      <div className="flex items-center justify-between border-b border-black/[.06] px-5 py-3">
        <div className="text-sm font-semibold">Área de parceiro</div>
        <span className="rounded-full bg-black/[.05] px-2.5 py-0.5 text-[11px] font-medium text-[#5d5d63]">Exemplo ilustrativo</span>
      </div>
      <div className="grid grid-cols-3 gap-px bg-black/[.06]">
        {[['Empresas a pagar', '12'], ['Disponível', '3 480 MT'], ['Este ano', '41 760 MT']].map(([l, v]) => (
          <div key={l} className="bg-white px-4 py-3"><div className="text-[11px] text-[#5d5d63]">{l}</div><div className="font-display text-lg font-bold tabular-nums">{v}</div></div>
        ))}
      </div>
      <ul className="divide-y divide-black/[.06] text-sm">
        {[['Ferragens Zambeze', 'Plano Empresa', '+ 290 MT'], ['Papelaria Central', 'Plano Essencial', '+ 130 MT'], ['Oficina Nhamayábuè', 'Plano Pro', '+ 590 MT']].map(([n, p, v]) => (
          <li key={n} className="flex items-center justify-between px-5 py-2.5"><div><div className="font-medium">{n}</div><div className="text-xs text-[#5d5d63]">{p}</div></div><span className="font-semibold tabular-nums text-emerald-700">{v}</span></li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2 border-t border-black/[.06] bg-[#fafafa] px-5 py-3 text-xs font-semibold">
        <span className="inline-flex items-center gap-1 rounded-lg bg-[#25D366] px-2.5 py-1.5 text-[#08331a]"><MessageCircle className="size-3.5" />Partilhar no WhatsApp</span>
        <span className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-black/10"><QrCode className="size-3.5" />QR code</span>
        <span className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 ring-1 ring-black/10"><Printer className="size-3.5" />Folheto</span>
      </div>
    </div>
  );
}

/**
 * Simulador: mostra o cálculo inteiro, sem letras pequenas. Mensal: 12 comissões
 * por empresa (a 1.ª com o desconto de boas-vindas de 10%). Anual: a empresa paga
 * o ano de uma vez e a comissão do ano chega de uma vez.
 */
function EarningsCalculator() {
  const [clients, setClients] = useState(10);
  const [plans, setPlans] = useState<{ code: string; name: string; price_monthly: string; price_yearly: string }[]>([]);
  const [plan, setPlan] = useState('empresa');
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [company, setCompany] = useState(false);
  useEffect(() => { get<{ plans: { code: string; name: string; price_monthly: string; price_yearly: string }[] }>('/reference/plans').then((r) => setPlans(r.plans.filter((p) => p.code !== 'trial' && Number(p.price_monthly) > 0))).catch(() => {}); }, []);
  const list = plans.length ? plans : [{ code: 'empresa', name: 'Empresa', price_monthly: '1450', price_yearly: '14500' }];
  const p = list.find((x) => x.code === plan) ?? list[0]!;
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const monthlyPrice = Number(p.price_monthly), yearlyPrice = Number(p.price_yearly);
  // Por empresa
  const perMonth = r2(monthlyPrice * 0.2);
  const firstMonth = r2(monthlyPrice * 0.9 * 0.2);
  const perYearMonthly = r2(firstMonth + perMonth * 11);
  const perYearYearly = r2(yearlyPrice * 0.9 * 0.2);
  const perCompanyYear = cycle === 'monthly' ? perYearMonthly : perYearYearly;
  const totalYear = r2(perCompanyYear * clients);
  const totalMonth = cycle === 'monthly' ? r2(perMonth * clients) : null;
  const withholding = company ? 0 : r2(totalYear * 0.2);
  const net = r2(totalYear - withholding);
  // 1450 → «1 450»; 261,5 → «261,50» (o pt-PT não agrupa números de quatro algarismos).
  const fmt = (n: number) => {
    const [int, dec] = (Math.round(n * 100) / 100).toFixed(2).split('.');
    return `${int!.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')}${dec === '00' ? '' : `,${dec}`}`;
  };
  const Seg = <T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: [T, string][] }) => (
    <div className="inline-flex flex-wrap rounded-xl bg-black/[.05] p-1 text-sm font-semibold" role="radiogroup">
      {options.map(([v, l]) => <button key={v} type="button" role="radio" aria-checked={value === v} onClick={() => onChange(v)} className={cn('rounded-lg px-3.5 py-1.5 transition', value === v ? 'bg-white text-[#111] shadow-sm' : 'text-[#5d5d63] hover:text-[#111]')}>{l}</button>)}
    </div>
  );
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.1fr]">
      <div className="space-y-6 rounded-2xl bg-white p-6 ring-1 ring-black/[.08] sm:p-7">
        <div>
          <div className="flex items-baseline justify-between"><label htmlFor="sim-clients" className="font-semibold">Empresas que trazem pagamento</label><span className="font-display text-2xl font-bold tabular-nums text-brand-700">{clients}</span></div>
          <input id="sim-clients" type="range" min={1} max={100} value={clients} onChange={(e) => setClients(Number(e.target.value))} className="mt-3 w-full accent-[#A6070C]" />
          <div className="mt-1 flex justify-between text-xs text-[#8a8a90]"><span>1</span><span>50</span><span>100</span></div>
        </div>
        <div><div className="mb-2 font-semibold">Plano das empresas</div><Seg value={plan} onChange={setPlan} options={list.map((x) => [x.code, `${x.name} · ${fmt(Number(x.price_monthly))} MT/mês`])} /></div>
        <div><div className="mb-2 font-semibold">Como pagam a subscrição</div><Seg value={cycle} onChange={setCycle} options={[['monthly', 'Todos os meses'], ['yearly', 'O ano de uma vez']]} /></div>
        <div><div className="mb-2 font-semibold">Eu sou</div><Seg value={company ? 'c' : 'p'} onChange={(v) => setCompany(v === 'c')} options={[['p', 'Pessoa singular'], ['c', 'Empresa']]} /></div>
      </div>

      <div className="relative overflow-hidden rounded-2xl bg-[#111] p-6 text-white shadow-2xl shadow-black/20 sm:p-7">
        <Watermark className="-bottom-16 -right-10 w-56 rotate-6 text-white/[.07]" />
        <div className="relative">
          <div className="text-sm text-white/60">Por cada empresa no plano {p.name}</div>
          {cycle === 'monthly' ? (
            <div className="mt-1 text-[15px] leading-relaxed">
              Paga <b>{fmt(monthlyPrice)} MT</b> por mês → <b className="text-brand-300">você recebe {fmt(perMonth)} MT por mês</b>, durante 12 meses.
              <div className="mt-1 text-sm text-white/55">No 1.º mês a empresa tem 10% de desconto, por isso a 1.ª comissão é {fmt(firstMonth)} MT. Total por empresa: <b className="text-white/80">{fmt(perYearMonthly)} MT</b>.</div>
            </div>
          ) : (
            <div className="mt-1 text-[15px] leading-relaxed">
              Paga o ano: {fmt(yearlyPrice)} MT − 10% de boas-vindas = <b>{fmt(r2(yearlyPrice * 0.9))} MT</b> → <b className="text-brand-300">você recebe {fmt(perYearYearly)} MT de uma vez</b>.
            </div>
          )}

          <div className="mt-6 grid gap-4 border-t border-white/10 pt-6 sm:grid-cols-2">
            {totalMonth != null
              ? <div><div className="text-sm text-white/60">Com {clients} {clients === 1 ? 'empresa' : 'empresas'}, por mês</div><div className="font-display text-3xl font-bold tabular-nums">{fmt(totalMonth)} <span className="text-base font-medium text-white/60">MT</span></div></div>
              : <div><div className="text-sm text-white/60">Comissão por empresa</div><div className="font-display text-3xl font-bold tabular-nums">{fmt(perYearYearly)} <span className="text-base font-medium text-white/60">MT</span></div></div>}
            <div><div className="text-sm text-white/60">Em 12 meses (bruto)</div><div className="font-display text-3xl font-bold tabular-nums text-brand-300">{fmt(totalYear)} <span className="text-base font-medium text-white/60">MT</span></div></div>
          </div>

          <dl className="mt-6 space-y-1.5 rounded-xl bg-white/[.06] p-4 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-white/60">{clients} × {fmt(perCompanyYear)} MT</dt><dd className="shrink-0 whitespace-nowrap tabular-nums">{fmt(totalYear)} MT</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-white/60">{company ? 'Retenção na fonte (empresa: não há — passa factura)' : 'Retenção de IRPS (20%), entregue às Finanças'}</dt><dd className="shrink-0 whitespace-nowrap tabular-nums">{company ? '0' : `− ${fmt(withholding)}`} MT</dd></div>
            <div className="flex justify-between gap-3 border-t border-white/10 pt-2 font-semibold"><dt>Recebe na sua conta, no 1.º ano</dt><dd className="shrink-0 whitespace-nowrap tabular-nums text-emerald-300">{fmt(net)} MT</dd></div>
          </dl>
          <p className="mt-4 text-xs leading-relaxed text-white/40">Simulação com os preços actuais. A comissão cobre no máximo os primeiros 12 meses de subscrição de cada empresa. As comissões ficam disponíveis 30 dias depois de cada pagamento e são pagas a partir de 1 000 MT. Se o cliente tiver outros descontos, a comissão acompanha o valor realmente pago.</p>
        </div>
      </div>
    </div>
  );
}

function ApplicationForm() {
  const [f, setF] = useState({ kind: 'accountant', name: '', isCompany: false, nuit: '', email: '', phone: '', city: '', message: '', acceptTerms: false, whatsappOptin: true });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { await post('/public/affiliates/apply', { ...f, nuit: f.nuit || null, city: f.city || null, message: f.message || null }); setSent(true); }
    catch (err) { setError(err instanceof ApiError && err.issues.length ? err.issues.map((i) => i.message).join(' · ') : err instanceof Error ? err.message : 'Não foi possível enviar.'); }
    finally { setBusy(false); }
  }
  return (
    <div className="rounded-2xl bg-white p-6 ring-1 ring-black/[.08] sm:p-8">
      {sent ? (
        <div className="py-10 text-center">
          <div className="mx-auto mb-4 grid size-14 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-700"><Check className="size-7" /></div>
          <h2 className="font-display text-2xl font-bold">Candidatura recebida.</h2>
          <p className="mx-auto mt-2 max-w-sm text-[#5d5d63]">Vamos analisá-la e responder para {f.email}, normalmente em 2 dias úteis.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <h2 className="font-display text-xl font-bold">Candidatar-me</h2>
          <Field label="Sou">
            <Select value={f.kind} onChange={set('kind')}>
              <option value="accountant">Contabilista / gabinete de contabilidade</option>
              <option value="reseller">Revendedor ou empresa de TI</option>
              <option value="individual">Consultor, formador ou outro</option>
            </Select>
          </Field>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
            <Field label={f.isCompany ? 'Nome da empresa' : 'Nome completo'}><Input required value={f.name} onChange={set('name')} /></Field>
            <div className="pb-2"><Checkbox checked={f.isCompany} onChange={(v) => setF({ ...f, isCompany: v })} label="Sou uma empresa" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="NUIT" hint="Para os pagamentos e a retenção na fonte"><Input inputMode="numeric" value={f.nuit} onChange={set('nuit')} placeholder="9 dígitos" /></Field>
            <Field label="Cidade"><Input value={f.city} onChange={set('city')} /></Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Email"><Input type="email" required value={f.email} onChange={set('email')} /></Field>
            <Field label="Telefone / WhatsApp"><Input type="tel" required value={f.phone} onChange={set('phone')} placeholder="84 123 4567" /></Field>
          </div>
          <Field label="Como pretende divulgar o Makila?" hint="Opcional"><Textarea rows={3} value={f.message} onChange={set('message')} placeholder="Ex.: tenho 25 empresas clientes no gabinete; dou formações em Tete…" /></Field>
          <Checkbox checked={f.acceptTerms} onChange={(v) => setF({ ...f, acceptTerms: v })}
            label={<>Li e aceito os <Link to="/parceiros/termos" target="_blank" className="font-medium text-brand-700 underline">Termos do Programa de Parceiros</Link></>} />
          <Checkbox checked={f.whatsappOptin} onChange={(v) => setF({ ...f, whatsappOptin: v })}
            label="Aceito receber mensagens da equipa Makila por WhatsApp sobre o programa (boas-vindas, materiais, pagamentos) e o convite para a comunidade de parceiros" />
          {error && <Alert tone="error">{error}</Alert>}
          <Button type="submit" className="w-full" size="lg" loading={busy} disabled={!f.acceptTerms}>Enviar candidatura</Button>
        </form>
      )}
    </div>
  );
}

export function PortalPartnerTerms() {
  const items: [string, string][] = [
    ['Adesão', 'A participação depende de aprovação do Makila (Ribeiro Tecnologias e Serviços, Lda.), excepto para clientes Makila, que aderem dentro da aplicação. O Makila pode recusar ou terminar a participação, com aviso por email.'],
    ['Comissão', 'O parceiro recebe a percentagem acordada (por omissão 20%) sobre o valor de cada pagamento de subscrição da empresa indicada, depois de descontos e sem IVA nem taxas de transacção, relativo aos primeiros meses de serviço acordados (por omissão 12), a contar do início do período pago pelo primeiro pagamento. Se um pagamento cobrir meses para lá desse período (ex.: 24 meses de uma vez, ou uma renovação antecipada), só a parte proporcional aos meses dentro dele dá comissão.'],
    ['Atribuição', 'Uma empresa fica associada ao parceiro cujo link usou nos 90 dias anteriores ao registo, ou cujo código escreveu no registo ou no primeiro pagamento. A associação não muda depois disso, salvo correcção pelo Makila com base em prova escrita.'],
    ['Exclusões', 'Não há comissão sobre a empresa do próprio parceiro, empresas com o mesmo NUIT, dono ou email do parceiro, nem empresas que já eram clientes pagantes.'],
    ['Pagamento', 'Cada comissão fica disponível 30 dias depois do pagamento que a originou. O Makila paga o saldo disponível a partir de 1 000 MT, por carteira móvel ou transferência, ou em crédito na subscrição do parceiro, conforme a sua escolha. Às comissões de pessoas singulares aplica-se a retenção na fonte de IRPS legalmente prevista; empresas parceiras emitem factura.'],
    ['Estornos', 'Se um pagamento for reembolsado, a comissão correspondente é anulada; se já tiver sido paga, o valor é descontado nos pagamentos seguintes.'],
    ['Conduta', 'É proibido: enviar mensagens não solicitadas em massa; comprar anúncios com a marca «Makila»; fazer-se passar pelo Makila; prometer funcionalidades, preços ou condições que não existem; usar o próprio link para obter desconto. A violação implica a perda das comissões não pagas e o fim da participação.'],
    ['Privacidade', 'O parceiro vê o nome, o plano e o estado das empresas que indicou e as suas comissões. Nunca vê documentos, clientes, valores facturados nem outros dados das empresas.'],
    ['Alterações', 'O Makila pode alterar estes termos com aviso de 30 dias por email. As condições já fixadas para empresas indicadas antes da alteração mantêm-se.'],
  ];
  return (
    <>
      <PageHero eyebrow="Programa de Parceiros" title="Termos do Programa de Parceiros">Versão de 3 de Novembro de 2026.</PageHero>
      <section className="mx-auto max-w-3xl space-y-6 px-4 py-14 sm:px-6">
        {items.map(([t, d], i) => (
          <div key={t}><h2 className="font-display text-lg font-bold">{i + 1}. {t}</h2><p className="mt-1.5 leading-relaxed text-[#3A3A3B]">{d}</p></div>
        ))}
      </section>
    </>
  );
}

// ── Painel do parceiro ──────────────────────────────────────────────────────
// Sessão própria (não é a da app): guardada só neste separador, 8 horas.
const PANEL_KEY = 'makila.partner';
const panelToken = () => { try { return sessionStorage.getItem(PANEL_KEY); } catch { return null; } };
async function panelFetch<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const r = await fetch(`/api${path}`, {
    method, headers: { 'x-requested-with': 'makila', authorization: `Bearer ${panelToken() ?? ''}`, ...(body ? { 'content-type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, j.code ?? 'http', j.message ?? `Erro ${r.status}`, j);
  return j as T;
}

export function PartnerLoginPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const t = params.get('t');
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A ligação é de uso único: trocar uma só vez (o StrictMode corre os efeitos duas vezes em desenvolvimento).
  const redeemed = useRef(false);
  useEffect(() => {
    if (!t || redeemed.current) return;
    redeemed.current = true;
    setBusy(true);
    api<{ token: string }>('POST', '/affiliate/session', { token: t })
      .then((r) => { try { sessionStorage.setItem(PANEL_KEY, r.token); } catch { /* sem armazenamento */ } nav('/parceiro', { replace: true }); })
      .catch((e) => { setError(e instanceof Error ? e.message : 'Ligação inválida'); setBusy(false); });
  }, [t, nav]);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { await post('/public/affiliates/login', { email }); setSent(true); } catch (err) { setError(err instanceof Error ? err.message : 'Erro'); } finally { setBusy(false); }
  }
  return (
    <>
      <PageHero eyebrow="Área de parceiro" title="Entrar na área de parceiro">Para parceiros Makila, sejam ou não assinantes. Enviamos uma ligação de entrada para o email com que se registou como parceiro — sem password.</PageHero>
      <section className="mx-auto max-w-md px-4 py-14 sm:px-6">
        {t && busy ? <Loading rows={2} /> : sent ? (
          <Alert tone="success" title="Verifique o seu email">Se <b>{email}</b> for o email de um parceiro Makila, enviámos uma ligação de entrada. É válida durante 30 minutos e só uma vez.</Alert>
        ) : (
          <form onSubmit={submit} className="space-y-4 rounded-2xl bg-white p-6 ring-1 ring-black/[.08]">
            {error && <Alert tone="error">{error}</Alert>}
            <Field label="Email de parceiro"><Input type="email" required autoFocus autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} leading={<Mail className="size-4" />} /></Field>
            <Button type="submit" className="w-full" size="lg" loading={busy}>Enviar ligação de entrada</Button>
            <p className="text-center text-sm text-[#5d5d63]">Ainda não é parceiro? <Link to="/parceiros#candidatura" className="font-semibold text-brand-700">Candidate-se</Link></p>
          </form>
        )}
      </section>
    </>
  );
}

export function PartnerPanelPage() {
  const [data, setData] = useState<PanelData | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  useEffect(() => { panelFetch<PanelData>('/affiliate/panel').then(setData).catch(setError); }, []);
  if (!panelToken() || error?.status === 401) return <Navigate to="/parceiro/entrar" replace />;
  return (
    <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
      {error ? <Alert tone="error">{error.message}</Alert> : !data ? <Loading /> : (
        <>
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-[13px] font-semibold uppercase tracking-[.16em] text-brand-700">Área de parceiro</div>
              <h1 className="font-display text-3xl font-bold">{data.affiliate.name}</h1>
            </div>
            <button type="button" className={cn(btn.ghost, 'h-10 text-sm')} onClick={() => { try { sessionStorage.removeItem(PANEL_KEY); } catch { /* */ } window.location.assign('/parceiros'); }}>Sair</button>
          </div>
          <AffiliatePanelView data={data}
            onSavePayout={async (body) => { const r = await panelFetch<PanelData>('/affiliate/payout', 'PUT', body); setData(r); }}
            onSaveWhatsapp={async (optin) => { const r = await panelFetch<PanelData>('/affiliate/whatsapp', 'PUT', { optin }); setData(r); }} />
        </>
      )}
    </section>
  );
}

export type PanelData = {
  affiliate: {
    name: string; kind: string; status: string; code: string | null; link: string; commission_pct: string; commission_months: number; withholding_pct: string;
    payout_method: string; payout_details: string | null; welcome_pct: string; can_credit: boolean; is_company: boolean; qr_svg: string | null;
    community_url: string | null; whatsapp_optin: boolean;
  };
  stats: { clicks_30d: number; signups: number; paying: number; pending: string; available: string; paid: string };
  companies: { name: string; plan: string | null; status: string | null; eligible: boolean; source: string; first_paid_on: string | null; commission_until: string | null; since: string }[];
  commissions: { id: string; kind: string; company: string; base_amount: string; pct: string; amount: string; status: string; available: boolean; available_on: string; created_at: string }[];
  payouts: { id: string; method: string; gross: string; withholding_amount: string; net: string; reference: string; paid_on: string }[];
  min_payout: string;
};
const COMPANY_STATUS: Record<string, string> = { trial: 'Em teste', active: 'Activa', past_due: 'Em atraso', read_only: 'Só leitura', suspended: 'Suspensa', closed: 'Encerrada' };
const PAYOUT_METHOD: Record<string, string> = { mpesa: 'M-Pesa', emola: 'e-Mola', mkesh: 'mKesh', transfer: 'Transferência', credit: 'Crédito', other: 'Outro' };

export type PayoutBody = { method: 'cash' | 'credit'; channel?: string | null; account?: string | null; holder?: string | null };

/**
 * Área de parceiro: resumo (link, números, empresas, comissões, pagamentos),
 * ferramentas de divulgação e dados para receber. Partilhada entre o site
 * (parceiros sem assinatura) e a app (clientes que aderiram).
 */
export function AffiliatePanelView({ data, compact, onSavePayout, onSaveWhatsapp }: { data: PanelData; compact?: boolean; onSavePayout: (b: PayoutBody) => Promise<void>; onSaveWhatsapp: (optin: boolean) => Promise<void> }) {
  const [tab, setTab] = useState<'resumo' | 'divulgar' | 'receber'>('resumo');
  const needsPayout = data.affiliate.payout_method === 'cash' && !data.affiliate.payout_details;
  return (
    <div className="space-y-6">
      <div className="flex gap-1 overflow-x-auto rounded-xl bg-black/[.04] p-1 text-sm font-semibold dark:bg-white/5" role="tablist">
        {([['resumo', 'Resumo', <Wallet key="r" className="size-4" />], ['divulgar', 'Ferramentas de divulgação', <Megaphone key="d" className="size-4" />], ['receber', 'Receber', <Landmark key="p" className="size-4" />]] as const).map(([id, label, icon]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
            className={cn('flex shrink-0 items-center gap-2 rounded-lg px-3.5 py-2 transition', tab === id ? 'bg-white text-[#111] shadow-sm dark:bg-white/15 dark:text-white' : 'text-[#5d5d63] hover:text-[#111] dark:text-white/60')}>
            {icon}{label}{id === 'receber' && needsPayout && <span className="size-2 rounded-full bg-brand-600" aria-label="por preencher" />}
          </button>
        ))}
      </div>
      {needsPayout && tab !== 'receber' && <Alert tone="warn" title="Diga-nos como quer receber">Indique a sua carteira móvel ou conta bancária no separador <button type="button" className="font-semibold underline" onClick={() => setTab('receber')}>Receber</button> para podermos pagar as comissões.</Alert>}
      {tab === 'resumo' && <><CommunityCard data={data} onSaveWhatsapp={onSaveWhatsapp} /><SummaryTab data={data} compact={compact} /></>}
      {tab === 'divulgar' && <PromoTools data={data} />}
      {tab === 'receber' && <PayoutTab data={data} onSave={onSavePayout} />}
    </div>
  );
}

/** Comunidade WhatsApp «Parceiros Makila» (só para aprovados), contacto com a equipa e consentimento. */
function CommunityCard({ data, onSaveWhatsapp }: { data: PanelData; onSaveWhatsapp: (optin: boolean) => Promise<void> }) {
  const a = data.affiliate;
  const [busy, setBusy] = useState(false);
  const team = waMe(CONTACT.phone, `Olá! Sou o parceiro ${a.code ?? ''} (${a.name}) do Programa de Parceiros Makila. `);
  if (a.status !== 'approved') return null;
  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-[#25D366]/[.08] p-5 ring-1 ring-[#25D366]/30 sm:flex-row sm:items-center">
      <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-[#25D366] text-white"><Users className="size-6" /></span>
      <div className="flex-1">
        <div className="font-semibold">Comunidade «Parceiros Makila» no WhatsApp</div>
        <p className="text-sm text-[#3A3A3B] dark:text-white/70">Materiais novos, novidades do Makila e um espaço para tirar dúvidas com a equipa e outros parceiros.</p>
        <label className="mt-2 inline-flex cursor-pointer items-center gap-2 text-xs text-[#5d5d63] dark:text-white/60">
          <input type="checkbox" checked={a.whatsapp_optin} disabled={busy} className="accent-[#1ea952]"
            onChange={async (e) => { setBusy(true); try { await onSaveWhatsapp(e.target.checked); } finally { setBusy(false); } }} />
          Aceito receber mensagens da equipa Makila por WhatsApp sobre o programa
        </label>
      </div>
      <div className="flex flex-wrap gap-2 sm:flex-col">
        {a.community_url && <a href={a.community_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-[#08331a] hover:brightness-95"><Users className="size-4" />Entrar na comunidade</a>}
        {team && <a href={team} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#111] ring-1 ring-black/10 hover:bg-black/[.03]"><MessageCircle className="size-4" />Falar com a equipa</a>}
      </div>
    </div>
  );
}

function SummaryTab({ data, compact }: { data: PanelData; compact?: boolean }) {
  const a = data.affiliate;
  const [copied, setCopied] = useState('');
  const copy = (v: string, what: string) => { void navigator.clipboard?.writeText(v); setCopied(what); setTimeout(() => setCopied(''), 1500); };
  const share = useMemo(() => `https://wa.me/?text=${encodeURIComponent(`Uso o Makila para facturar de acordo com a lei, com M-Pesa e a comunicação à AT num botão. Registe-se pelo meu link e tem ${a.welcome_pct}% de desconto no primeiro pagamento: ${a.link}`)}`, [a]);
  return (
    <div className="space-y-6">
      {a.status === 'suspended' && <Alert tone="warn" title="Participação suspensa">As indicações novas não geram comissão enquanto a participação estiver suspensa. Contacte o suporte do Makila.</Alert>}
      <div className="rounded-2xl bg-[#111] p-6 text-white">
        <div className="grid gap-5 md:grid-cols-[1.5fr_1fr] md:items-center">
          <div className="min-w-0">
            <div className="text-sm text-white/60">O seu link de parceiro</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <code className="min-w-0 truncate rounded-lg bg-white/10 px-3 py-2 font-mono text-sm">{a.link}</code>
              <button type="button" onClick={() => copy(a.link, 'link')} className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#111] hover:bg-white/90"><Copy className="size-4" />{copied === 'link' ? 'Copiado' : 'Copiar'}</button>
              <a href={share} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-2 text-sm font-semibold text-[#08331a] hover:brightness-95"><MessageCircle className="size-4" />WhatsApp</a>
            </div>
            <p className="mt-2 text-sm text-white/60">Código <button type="button" onClick={() => copy(a.code ?? '', 'code')} className="font-mono font-semibold text-white underline decoration-dotted underline-offset-4">{a.code}</button>{copied === 'code' ? ' · copiado' : ''} — quem o usar tem {a.welcome_pct}% no primeiro pagamento.</p>
          </div>
          <div className="text-sm text-white/70 md:text-right">
            <div><b className="text-white">{Number(a.commission_pct).toLocaleString('pt-PT')}%</b> durante <b className="text-white">{a.commission_months} meses</b></div>
            <div>Pagamento: {a.payout_method === 'credit' ? 'crédito na subscrição' : 'dinheiro'}{Number(a.withholding_pct) > 0 ? ` · retenção ${Number(a.withholding_pct).toLocaleString('pt-PT')}%` : ''}</div>
          </div>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat icon={<MousePointerClick />} label="Cliques (30 dias)" value={String(data.stats.clicks_30d)} />
        <Stat icon={<Building2 />} label="Empresas registadas" value={String(data.stats.signups)} />
        <Stat icon={<Repeat />} label="Empresas a pagar" value={String(data.stats.paying)} />
        <Stat icon={<CalendarClock />} label="Em retenção (30 d)" value={`${mt(data.stats.pending)} MT`} />
        <Stat icon={<Wallet />} label="Disponível" value={`${mt(data.stats.available)} MT`} tone={Number(data.stats.available) >= Number(data.min_payout) ? 'green' : undefined} />
        <Stat icon={<Check />} label="Já pago" value={`${mt(data.stats.paid)} MT`} />
      </div>
      <p className="-mt-3 text-xs text-[#5d5d63]">Cada comissão fica disponível 30 dias depois do pagamento da empresa. Pagamos a partir de {mt(data.min_payout)} MT disponíveis.</p>

      <div className={cn('grid gap-6', !compact && 'lg:grid-cols-2')}>
        <Panel title="Empresas indicadas">
          {!data.companies.length ? <Empty>Ainda nenhuma. Partilhe o seu link!</Empty> : (
            <ul className="divide-y divide-black/[.06]">{data.companies.map((c, i) => (
              <li key={i} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <div className="min-w-0"><div className="truncate font-medium">{c.name}</div>
                  <div className="text-xs text-[#5d5d63]">desde {date(c.since)}{c.commission_until ? ` · comissão até ${date(c.commission_until)}` : c.eligible ? ' · ainda sem pagamento' : ''}</div></div>
                {!c.eligible ? <Badge tone="gray">Sem comissão</Badge> : <Badge tone={c.status === 'active' ? 'green' : c.status === 'trial' ? 'blue' : 'gray'} dot>{COMPANY_STATUS[c.status ?? ''] ?? c.status}</Badge>}
              </li>))}</ul>
          )}
        </Panel>
        <Panel title="Comissões">
          {!data.commissions.length ? <Empty>As comissões aparecem aqui quando as empresas pagarem.</Empty> : (
            <ul className="divide-y divide-black/[.06]">{data.commissions.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <div className="min-w-0"><div className="truncate font-medium">{c.company}</div>
                  <div className="text-xs text-[#5d5d63]">{c.kind === 'adjustment' ? 'Estorno (pagamento reembolsado)' : `${Number(c.pct).toLocaleString('pt-PT')}% de ${mt(c.base_amount)} MT`} · {date(c.created_at)}</div></div>
                <div className="text-right"><div className={cn('font-semibold tabular-nums', Number(c.amount) < 0 && 'text-red-700')}>{mt(c.amount)} MT</div>
                  <div className="text-xs">{c.status === 'paid' ? <span className="text-emerald-700">Paga</span> : c.status === 'reversed' ? <span className="text-[#5d5d63]">Anulada</span> : c.available ? <span className="text-emerald-700">Disponível</span> : <span className="text-[#5d5d63]">Disponível a {date(c.available_on)}</span>}</div></div>
              </li>))}</ul>
          )}
        </Panel>
      </div>
      {data.payouts.length > 0 && (
        <Panel title="Pagamentos recebidos">
          <ul className="divide-y divide-black/[.06]">{data.payouts.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <div><div className="font-medium">{date(p.paid_on)} · {PAYOUT_METHOD[p.method] ?? p.method}</div><div className="text-xs text-[#5d5d63]">Ref. {p.reference}{Number(p.withholding_amount) > 0 ? ` · retenção IRPS ${mt(p.withholding_amount)} MT sobre ${mt(p.gross)} MT` : ''}</div></div>
              <div className="font-semibold tabular-nums">{mt(p.net)} MT</div>
            </li>))}</ul>
        </Panel>
      )}
    </div>
  );
}

// ── Ferramentas de divulgação ───────────────────────────────────────────────
type Template = { id: string; title: string; hint: string; subject?: string; text: string; actions: { label: string; href: string; tone?: 'wa' }[] };

function templates(a: PanelData['affiliate']): Template[] {
  const pct = a.welcome_pct;
  const wa = (t: string) => `https://wa.me/?text=${encodeURIComponent(t)}`;
  const short = `Uso o Makila para facturar: facturas e recibos com QR, M-Pesa e e-Mola, e o ficheiro para a AT num clique. Experimente 14 dias grátis e tenha ${pct}% de desconto no primeiro pagamento pelo meu link: ${a.link}`;
  const clients = `Caro cliente,\n\nA facturação tem de seguir as regras da Autoridade Tributária, com documentos numerados e o ficheiro mensal para o e-Declaração. Recomendo o Makila: é feito em Moçambique, funciona no telemóvel ou no computador, aceita M-Pesa e e-Mola, e prepara o ficheiro da AT num clique.\n\nPode experimentar 14 dias grátis e, registando-se por este link, tem ${pct}% de desconto no primeiro pagamento:\n${a.link}\n\nSe preferir, use o código ${a.code} ao registar-se.\n\nCumprimentos,\n${a.name}`;
  const emailBody = `Olá,\n\nQueria recomendar-lhe o Makila, o programa de facturação que recomendo. Emite facturas e recibos com QR, regista os pagamentos M-Pesa e e-Mola e gera o ficheiro mensal para a AT num clique.\n\nExperimente 14 dias grátis, sem cartão. Pelo meu link tem ${pct}% de desconto no primeiro pagamento:\n${a.link}\n\nCódigo de parceiro: ${a.code}\n\nCumprimentos,\n${a.name}`;
  const post = `Facturar de acordo com a lei já não precisa de ser complicado. 🇲🇿\n\nO Makila emite facturas e recibos com QR, aceita M-Pesa e e-Mola e prepara o ficheiro da AT num clique — no telemóvel ou no computador.\n\n✅ 14 dias grátis\n✅ ${pct}% de desconto no primeiro pagamento com o código ${a.code}\n\n👉 ${a.link}`;
  return [
    { id: 'wa', title: 'WhatsApp — mensagem curta', hint: 'Para contactos e grupos.', text: short, actions: [{ label: 'Enviar por WhatsApp', href: wa(short), tone: 'wa' }] },
    { id: 'clientes', title: 'Para os seus clientes', hint: 'Ideal para contabilistas e consultores: explica porquê e como.', text: clients, actions: [{ label: 'Enviar por WhatsApp', href: wa(clients), tone: 'wa' }] },
    { id: 'email', title: 'Email', hint: 'Assunto e texto prontos a enviar.', subject: 'Recomendo o Makila para a sua facturação', text: emailBody,
      actions: [{ label: 'Abrir no email', href: `mailto:?subject=${encodeURIComponent('Recomendo o Makila para a sua facturação')}&body=${encodeURIComponent(emailBody)}` }] },
    { id: 'redes', title: 'Publicação para redes sociais', hint: 'Facebook, LinkedIn, Instagram ou estado do WhatsApp.', text: post,
      actions: [
        { label: 'Facebook', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(a.link)}` },
        { label: 'LinkedIn', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(a.link)}` },
      ] },
  ];
}

/** SVG do QR (gerado pela API com a biblioteca qrcode) → PNG em alta resolução. */
async function svgToPng(svg: string, size = 1200): Promise<Blob> {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    await new Promise<void>((ok, fail) => { img.onload = () => ok(); img.onerror = () => fail(new Error('QR')); img.src = url; });
    const c = Object.assign(document.createElement('canvas'), { width: size, height: size });
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, size, size);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, 0, 0, size, size);
    return await new Promise((ok) => c.toBlob((b) => ok(b!), 'image/png'));
  } finally { URL.revokeObjectURL(url); }
}
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  Object.assign(document.createElement('a'), { href: url, download: name }).click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
const escHtml = (v: string) => v.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

/** Folheto A4 para imprimir ou guardar em PDF: logótipo, vantagens, QR e código do parceiro. */
function printFlyer(a: PanelData['affiliate']) {
  const w = window.open('', '_blank');
  if (!w) return;
  const logo = `${window.location.origin}/brand/makila-horizontal-cor.svg`;
  w.document.write(`<!doctype html><html lang="pt"><head><meta charset="utf-8"><title>Makila — ${escHtml(a.code ?? '')}</title>
<style>
  @page { size: A4; margin: 14mm }
  * { box-sizing: border-box } body { margin: 0; font-family: 'Inter', system-ui, sans-serif; color: #111 }
  .sheet { min-height: 265mm; display: flex; flex-direction: column; border-top: 8px solid #A6070C; padding: 14mm 4mm 0 }
  img.logo { height: 64px } h1 { font-size: 34px; line-height: 1.15; margin: 14mm 0 4mm } h1 span { color: #A6070C }
  p.lead { font-size: 17px; color: #444; margin: 0 0 9mm; max-width: 150mm }
  ul { list-style: none; padding: 0; margin: 0 0 10mm; display: grid; grid-template-columns: 1fr 1fr; gap: 5mm 8mm }
  li { font-size: 15px; padding-left: 9mm; position: relative } li::before { content: '✓'; position: absolute; left: 0; color: #A6070C; font-weight: 800 }
  .offer { margin-top: auto; display: flex; gap: 10mm; align-items: center; background: #111; color: #fff; border-radius: 6mm; padding: 9mm }
  .qr { background: #fff; padding: 3mm; border-radius: 3mm; width: 52mm; height: 52mm } .qr svg { width: 100%; height: 100% }
  .offer b { font-size: 26px; display: block } .offer .code { font-family: ui-monospace, monospace; font-size: 22px; color: #ffb4b4 }
  .small { font-size: 12px; color: #bbb; margin-top: 3mm } .by { margin: 6mm 0 0; font-size: 13px; color: #666 }
</style></head><body><div class="sheet">
  <img class="logo" src="${logo}" alt="Makila Facturação">
  <h1>Facture de acordo com a lei.<br><span>Em minutos.</span></h1>
  <p class="lead">O programa de facturação feito em Moçambique, para empresas que vendem em meticais. No telemóvel ou no computador.</p>
  <ul>
    <li>Facturas e recibos com QR verificável</li><li>Pagamentos M-Pesa e e-Mola registados</li>
    <li>Ficheiro mensal da AT num clique</li><li>Contas correntes e stock no mesmo sítio</li>
    <li>Envio por email e WhatsApp</li><li>14 dias grátis, sem cartão</li>
  </ul>
  <div class="offer">
    <div class="qr">${a.qr_svg ?? ''}</div>
    <div><b>${escHtml(a.welcome_pct)}% de desconto no primeiro pagamento</b>
      <div style="margin-top:3mm;font-size:15px">Aponte a câmara ao código ou use o código de parceiro</div>
      <div class="code">${escHtml(a.code ?? '')}</div>
      <div class="small">${escHtml(a.link)}</div></div>
  </div>
  <p class="by">Recomendado por ${escHtml(a.name)} — parceiro Makila</p>
</div><script>window.onload = () => setTimeout(() => window.print(), 300)</script></body></html>`);
  w.document.close();
}

function PromoTools({ data }: { data: PanelData }) {
  const a = data.affiliate;
  const [copied, setCopied] = useState('');
  const copy = (v: string, id: string) => { void navigator.clipboard?.writeText(v); setCopied(id); setTimeout(() => setCopied(''), 1500); };
  const list = useMemo(() => templates(a), [a]);
  if (!a.code) return <Alert tone="info">As ferramentas ficam disponíveis quando a sua candidatura for aprovada.</Alert>;
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
        <Panel title="Mensagens prontas">
          <div className="divide-y divide-black/[.06] dark:divide-white/10">
            {list.map((t) => (
              <div key={t.id} className="px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div><div className="font-semibold">{t.title}</div><div className="text-xs text-[#5d5d63] dark:text-white/60">{t.hint}</div></div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => copy(t.subject ? `${t.subject}\n\n${t.text}` : t.text, t.id)} className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ring-black/15 hover:bg-black/[.04] dark:ring-white/20"><Copy className="size-4" />{copied === t.id ? 'Copiado' : 'Copiar'}</button>
                    {t.actions.map((x) => (
                      <a key={x.label} href={x.href} target="_blank" rel="noopener noreferrer" className={cn('inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold', x.tone === 'wa' ? 'bg-[#25D366] text-[#08331a] hover:brightness-95' : 'bg-[#111] text-white hover:bg-black dark:bg-white/15')}>
                        {x.tone === 'wa' ? <MessageCircle className="size-4" /> : <Share2 className="size-4" />}{x.label}</a>
                    ))}
                  </div>
                </div>
                {t.subject && <div className="mt-3 text-sm"><span className="text-[#5d5d63]">Assunto:</span> {t.subject}</div>}
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-lg bg-black/[.03] p-3 font-sans text-[13px] leading-relaxed text-[#3A3A3B] dark:bg-white/5 dark:text-white/80">{t.text}</pre>
              </div>
            ))}
          </div>
        </Panel>
        <div className="space-y-6">
          <Panel title="QR code do seu link">
            <div className="p-5">
              <div className="mx-auto w-48 rounded-xl bg-white p-3 ring-1 ring-black/[.08] [&_svg]:h-auto [&_svg]:w-full" aria-label={`QR code de ${a.link}`} role="img"
                dangerouslySetInnerHTML={{ __html: a.qr_svg ?? '' }} />
              <p className="mt-3 text-center text-xs text-[#5d5d63] dark:text-white/60">Para cartões de visita, montras, apresentações e formações. Quem o ler abre o seu link.</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button type="button" onClick={async () => saveBlob(await svgToPng(a.qr_svg ?? ''), `makila-${a.code}-qr.png`)} className={cn(btn.ghost, 'h-10 text-sm')}><Download className="size-4" />PNG</button>
                <button type="button" onClick={() => saveBlob(new Blob([a.qr_svg ?? ''], { type: 'image/svg+xml' }), `makila-${a.code}-qr.svg`)} className={cn(btn.ghost, 'h-10 text-sm')}><QrCode className="size-4" />SVG</button>
              </div>
            </div>
          </Panel>
          <Panel title="Folheto para imprimir">
            <div className="p-5">
              <p className="text-sm text-[#5d5d63] dark:text-white/60">Folheto A4 com as vantagens do Makila, o seu QR code e o seu código. Imprima ou guarde em PDF para enviar.</p>
              <button type="button" onClick={() => printFlyer(a)} className={cn(btn.primary, 'mt-4 h-11 w-full text-sm')}><Printer className="size-4" />Abrir folheto</button>
            </div>
          </Panel>
          <Panel title="Logótipos">
            <ul className="divide-y divide-black/[.06] text-sm dark:divide-white/10">
              {[['makila-horizontal-cor.svg', 'A cores (fundo claro)'], ['makila-horizontal-negativo.svg', 'Negativo (fundo escuro)'], ['makila-horizontal-mono-preto.svg', 'Monocromático'], ['makila-simbolo.svg', 'Símbolo']].map(([f, l]) => (
                <li key={f}><a href={`/brand/${f}`} download className="flex items-center justify-between px-5 py-2.5 hover:bg-black/[.03] dark:hover:bg-white/5"><span>{l}</span><Download className="size-4 text-[#5d5d63]" /></a></li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>
      <Panel title="Boas práticas">
        <div className="grid gap-6 p-5 text-sm md:grid-cols-2">
          <div><div className="mb-2 font-semibold text-emerald-700 dark:text-emerald-400">Funciona bem</div>
            <ul className="space-y-1.5 text-[#3A3A3B] dark:text-white/80">
              <li>✓ Falar com quem conhece: clientes, colegas, grupos de empresários.</li>
              <li>✓ Mostrar o Makila a funcionar no telemóvel — a primeira factura sai em minutos.</li>
              <li>✓ Lembrar o desconto de {a.welcome_pct}% e os 14 dias grátis.</li>
              <li>✓ Usar o QR em formações, apresentações e no balcão.</li>
            </ul></div>
          <div><div className="mb-2 font-semibold text-red-700 dark:text-red-400">Não é permitido</div>
            <ul className="space-y-1.5 text-[#3A3A3B] dark:text-white/80">
              <li>✗ Enviar mensagens em massa a quem não o conhece.</li>
              <li>✗ Comprar anúncios com a marca «Makila».</li>
              <li>✗ Fazer-se passar pelo Makila ou prometer o que não existe.</li>
              <li>✗ Usar o seu próprio link para ter desconto.</li>
            </ul>
            <Link to="/parceiros/termos" target="_blank" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:underline"><FileText className="size-4" />Termos do Programa de Parceiros</Link></div>
        </div>
      </Panel>
    </div>
  );
}

// ── Receber ─────────────────────────────────────────────────────────────────
const CHANNELS = [['mpesa', 'M-Pesa'], ['emola', 'e-Mola'], ['mkesh', 'mKesh'], ['bank', 'Transferência bancária']] as const;
function PayoutTab({ data, onSave }: { data: PanelData; onSave: (b: PayoutBody) => Promise<void> }) {
  const a = data.affiliate;
  const [method, setMethod] = useState<'cash' | 'credit'>(a.payout_method === 'credit' ? 'credit' : 'cash');
  const [channel, setChannel] = useState<string>('mpesa');
  const [account, setAccount] = useState('');
  const [holder, setHolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setMsg(null);
    try { await onSave(method === 'credit' ? { method } : { method, channel, account, holder: holder || null }); setMsg({ tone: 'success', text: 'Dados de pagamento guardados.' }); setAccount(''); setHolder(''); }
    catch (err) { setMsg({ tone: 'error', text: err instanceof Error ? err.message : 'Não foi possível guardar.' }); }
    finally { setBusy(false); }
  }
  return (
    <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
      <Panel title="Como quer receber as comissões">
        <form onSubmit={submit} className="space-y-4 p-5">
          <div className="rounded-xl bg-black/[.03] p-4 text-sm dark:bg-white/5">
            <div className="text-[#5d5d63] dark:text-white/60">Actualmente</div>
            <div className="font-semibold">{a.payout_method === 'credit' ? 'Crédito na subscrição da sua empresa' : a.payout_details ?? 'Ainda não indicado'}</div>
          </div>
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Forma de pagamento">
            {([['cash', 'Dinheiro', 'Carteira móvel ou conta bancária'], ['credit', 'Crédito na subscrição', a.can_credit ? 'Desconta nas próximas mensalidades' : 'Só para clientes Makila']] as const).map(([id, t, d]) => (
              <button key={id} type="button" role="radio" aria-checked={method === id} disabled={id === 'credit' && !a.can_credit} onClick={() => setMethod(id)}
                className={cn('rounded-xl p-3 text-left ring-1 ring-inset transition disabled:opacity-50', method === id ? 'ring-2 ring-brand-600' : 'ring-black/15 hover:ring-brand-400 dark:ring-white/20')}>
                <div className="font-semibold">{t}</div><div className="text-xs text-[#5d5d63] dark:text-white/60">{d}</div>
              </button>
            ))}
          </div>
          {method === 'cash' && <>
            <Field label="Receber por"><Select value={channel} onChange={(e) => setChannel(e.target.value)}>{CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</Select></Field>
            <Field label={channel === 'bank' ? 'Banco e NIB' : 'Número da carteira'}><Input required value={account} onChange={(e) => setAccount(e.target.value)} placeholder={channel === 'bank' ? 'BCI · 0008 0000 1234 5678 910 13' : channel === 'emola' ? '86 123 4567' : channel === 'mkesh' ? '82 123 4567' : '84 123 4567'} /></Field>
            {channel === 'bank' && <Field label="Titular da conta"><Input required value={holder} onChange={(e) => setHolder(e.target.value)} /></Field>}
          </>}
          {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
          <Button type="submit" loading={busy}>Guardar</Button>
        </form>
      </Panel>
      <Panel title="Como funcionam os pagamentos">
        <ul className="space-y-3 p-5 text-sm text-[#3A3A3B] dark:text-white/80">
          <li>• Cada comissão fica <b>disponível 30 dias</b> depois do pagamento da empresa (o prazo cobre reembolsos).</li>
          <li>• Pagamos o saldo disponível a partir de <b>{mt(data.min_payout)} MT</b>.</li>
          {Number(a.withholding_pct) > 0
            ? <li>• Às comissões de pessoas singulares aplica-se a <b>retenção na fonte de IRPS ({Number(a.withholding_pct).toLocaleString('pt-PT')}%)</b>, que entregamos às Finanças em seu nome. O valor retido aparece em cada pagamento.</li>
            : a.is_company ? <li>• Como empresa parceira, emite uma <b>factura</b> à Ribeiro Tecnologias e Serviços pelo valor das comissões.</li> : null}
          <li>• Os pagamentos recebidos aparecem no separador Resumo, com a referência da transacção.</li>
        </ul>
      </Panel>
    </div>
  );
}

function Stat({ icon, label, value, tone }: { icon: ReactNode; label: string; value: string; tone?: 'green' }) {
  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-black/[.08] dark:bg-white/5 dark:ring-white/10">
      <div className="flex items-center gap-2 text-xs text-[#5d5d63] dark:text-white/60 [&>svg]:size-4">{icon}{label}</div>
      <div className={cn('mt-1 font-display text-xl font-bold tabular-nums', tone === 'green' && 'text-emerald-700 dark:text-emerald-400')}>{value}</div>
    </div>
  );
}
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-black/[.08] dark:bg-white/5 dark:ring-white/10">
      <h2 className="border-b border-black/[.06] px-5 py-3 font-semibold dark:border-white/10">{title}</h2>
      {children}
    </div>
  );
}
const Empty = ({ children }: { children: ReactNode }) => <p className="px-5 py-8 text-center text-sm text-[#5d5d63]">{children}</p>;

